/**
 * Posturax FAQ Accordion Component
 * Maneja la funcionalidad de expandir/contraer las preguntas frecuentes
 */

class PosturaxFAQAccordion extends HTMLElement {
  constructor() {
    super();
    
    this.accordionItems = this.querySelectorAll('[data-faq-item]');
    this.allowMultiple = this.dataset.allowMultiple === 'true';
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupAccessibility();
  }

  bindEvents() {
    this.accordionItems.forEach((item, index) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const content = item.querySelector('[data-faq-content]');
      
      if (trigger && content) {
        trigger.addEventListener('click', () => this.toggleItem(item, trigger, content));
        
        // Permitir navegación con teclado
        trigger.addEventListener('keydown', (e) => {
          this.handleKeyNavigation(e, index);
        });
      }
    });
  }

  setupAccessibility() {
    this.accordionItems.forEach((item, index) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const content = item.querySelector('[data-faq-content]');
      
      if (trigger && content) {
        // Asegurar IDs únicos
        if (!content.id) {
          content.id = `faq-content-${index}-${Date.now()}`;
        }
        
        if (!trigger.id) {
          trigger.id = `faq-trigger-${index}-${Date.now()}`;
        }
        
        // Configurar ARIA
        trigger.setAttribute('aria-controls', content.id);
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-labelledby', trigger.id);
        content.setAttribute('aria-hidden', 'true');
      }
    });
  }

  toggleItem(item, trigger, content) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    
    // Si no permitimos múltiples abiertos, cerrar todos los demás
    if (!this.allowMultiple && !isOpen) {
      this.closeAllItems();
    }
    
    if (isOpen) {
      this.closeItem(item, trigger, content);
    } else {
      this.openItem(item, trigger, content);
    }
  }

  openItem(item, trigger, content) {
    // Actualizar estados ARIA
    trigger.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');
    
    // Agregar clases para CSS
    item.classList.add('is-open');
    content.classList.add('is-open');
    
    // Calcular altura y animar
    const height = content.scrollHeight;
    content.style.maxHeight = `${height}px`;
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('faq:opened', {
      detail: { item, trigger, content }
    }));
  }

  closeItem(item, trigger, content) {
    // Actualizar estados ARIA
    trigger.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    
    // Remover clases
    item.classList.remove('is-open');
    content.classList.remove('is-open');
    
    // Animar cierre
    content.style.maxHeight = '0px';
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('faq:closed', {
      detail: { item, trigger, content }
    }));
  }

  closeAllItems() {
    this.accordionItems.forEach(item => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const content = item.querySelector('[data-faq-content]');
      
      if (trigger && content && trigger.getAttribute('aria-expanded') === 'true') {
        this.closeItem(item, trigger, content);
      }
    });
  }

  openAllItems() {
    this.accordionItems.forEach(item => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const content = item.querySelector('[data-faq-content]');
      
      if (trigger && content && trigger.getAttribute('aria-expanded') === 'false') {
        this.openItem(item, trigger, content);
      }
    });
  }

  handleKeyNavigation(event, currentIndex) {
    const { key } = event;
    let targetIndex = currentIndex;
    
    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = currentIndex + 1;
        if (targetIndex >= this.accordionItems.length) {
          targetIndex = 0; // Wrap to first
        }
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) {
          targetIndex = this.accordionItems.length - 1; // Wrap to last
        }
        break;
        
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
        
      case 'End':
        event.preventDefault();
        targetIndex = this.accordionItems.length - 1;
        break;
        
      default:
        return; // No action needed
    }
    
    // Focus on target trigger
    const targetTrigger = this.accordionItems[targetIndex]?.querySelector('[data-faq-trigger]');
    if (targetTrigger) {
      targetTrigger.focus();
    }
  }

  // Public API methods
  expandItem(index) {
    const item = this.accordionItems[index];
    if (!item) return;
    
    const trigger = item.querySelector('[data-faq-trigger]');
    const content = item.querySelector('[data-faq-content]');
    
    if (trigger && content && trigger.getAttribute('aria-expanded') === 'false') {
      this.openItem(item, trigger, content);
    }
  }

  collapseItem(index) {
    const item = this.accordionItems[index];
    if (!item) return;
    
    const trigger = item.querySelector('[data-faq-trigger]');
    const content = item.querySelector('[data-faq-content]');
    
    if (trigger && content && trigger.getAttribute('aria-expanded') === 'true') {
      this.closeItem(item, trigger, content);
    }
  }

  expandAll() {
    this.openAllItems();
  }

  collapseAll() {
    this.closeAllItems();
  }
}

// Auto-registrar el custom element
if (!customElements.get('posturax-faq-accordion')) {
  customElements.define('posturax-faq-accordion', PosturaxFAQAccordion);
}

// Funciones helper para analytics (opcional)
document.addEventListener('faq:opened', (event) => {
  const question = event.detail.trigger.querySelector('.posturax-faq__question-text')?.textContent;
  
  // Track con Google Analytics si está disponible
  if (typeof gtag !== 'undefined') {
    gtag('event', 'faq_opened', {
      'custom_parameter': question
    });
  }
  
  // Track con Facebook Pixel si está disponible
  if (typeof fbq !== 'undefined') {
    fbq('trackCustom', 'FAQ_Opened', {
      question: question
    });
  }
});

// Smooth scroll to FAQ from external links
function scrollToFAQ(questionIndex = null) {
  const faqSection = document.querySelector('.posturax-faq');
  
  if (faqSection) {
    faqSection.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
    
    // Si se especifica un índice, expandir esa pregunta después del scroll
    if (questionIndex !== null) {
      setTimeout(() => {
        const accordion = faqSection.querySelector('posturax-faq-accordion');
        if (accordion) {
          accordion.expandItem(questionIndex);
        }
      }, 500);
    }
  }
}

// Hacer la función disponible globalmente
window.scrollToFAQ = scrollToFAQ;

export default PosturaxFAQAccordion;
